// @mui material components
import { History } from "@mui/icons-material";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import InvoiceContext from "context/InvoiceContext";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/ApiService";
import StorageService from "services/StorageService";

// Soft UI Dashboard PRO React base styles
import colors from "assets/theme/base/colors";

import Swal from "sweetalert2";
import Constants from "utils/Constants";
import Helpers from "utils/Helpers";
import ToastUtils from "utils/ToastUtils";

function CompaniesActionCell({ apiKey, companyId, companyName, refreshTable }) {
  const accessLevel = StorageService.getAccessLevel();
  const { invoiceCount, setInvoiceCount } = useContext(InvoiceContext);
  const navigate = useNavigate();
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditTransactions, setCreditTransactions] = useState([]);
  const { badgeColors } = colors;

  const handleOpenCreditTransactions = () => {
    setCreditDialogOpen(true);
    getCreditTransactions();
  };

  const handleCloseCreditTransactions = () => {
    setCreditDialogOpen(false);
  };

  const getCreditTransactions = async () => {
    const response = await ApiService.getData(
      `/api/v1/credit-transactions?companyId=${companyId}&type=USER_TO_COMPANY&enrich=true`
    );
    if (!response) {
      ToastUtils.error("Αδυναμία εύρεσης συναλλαγών credits!");
      return;
    }

    const transactions = response.data.data;
    transactions.map((el) => {
      el.createdAt = Helpers.formatDateToNormal(el.createdAt);
    });
    setCreditTransactions(transactions);
  };

  const clickApiKey = () => {
    Swal.fire({
      icon: "warning",
      title: "API Key",
      text: "Μπορείτε να αντιγράψετε το ενεργό κλειδί πατώντας αντιγραφή ή να το ανανεώσετε πατώντας ανανέωση. Προσοχή, μετά την ανανέωση όλα τα παρελθοντικά κλειδιά χάνουν τη δυνατότητα διαβίβασης!",
      showCancelButton: true,
      confirmButtonText: "ΑΝΤΙΓΡΑΦΗ",
      showDenyButton: true,
      denyButtonText: "ΑΝΑΝΕΩΣΗ",
      cancelButtonText: "ΑΚΥΡΟ",
      preDeny: async () => {
        const response = await ApiService.postData(
          `/api/v1/companies/${companyId}/refresh-api-key`
        );

        if (!response) {
          Swal.fire({
            icon: "error",
            title: "Αποτυχία!",
            text: "Αποτυχία ανανέωσης κλειδιού!",
          });
          return false;
        }
        navigator.clipboard.writeText(response.data.apiKey);

        return true;
      },
    }).then((result) => {
      if (result.isConfirmed) {
        navigator.clipboard.writeText(apiKey);
        Swal.fire({
          icon: "success",
          title: "Επιτυχία!",
          text: "Το κλειδί διαβίβασης αντιγράφηκε στο πρόχειρο!",
        });
      }
      if (result.isDenied) {
        Swal.fire({
          icon: "success",
          title: "Επιτυχία!",
          text: "Το κλειδί διαβίβασης ανανεώθηκε! Το νέο κλειδί έχει αντιγραφεί στο πρόχειρο.",
        });
      }
    });
  };

  const handleDelete = () => {
    Swal.fire({
      icon: "warning",
      title: "Διαγραφή Εταιρείας?",
      text: "Σίγουρα θέλετε να διαγράψετε την εταιρεία? Μετά τη διαγραφή η διαβίβαση παραστατικών δεν θα είναι δυνατή! Τα διαβιβασμένα παραστατικά θα είναι πάντα διαθέσιμα για προβολή.",
      showCancelButton: true,
      confirmButtonText: "ΔΙΑΓΡΑΦΗ",
      cancelButtonText: "ΑΚΥΡΟ",
    }).then((result) => {
      if (!result.isConfirmed) return;
      deleteCompany(companyId);
    });
  };

  const deleteCompany = async (companyId) => {
    const reponse = await ApiService.deleteData(`/api/v1/companies/${companyId}`);
    if (!reponse) {
      Swal.fire({
        icon: "error",
        title: "Αποτυχία!",
        text: "Αδυναμία διαγραφής εταιρείας! Δοκιμάστε ξανά αργότερα.",
      });
      return;
    }
    Swal.fire({
      icon: "success",
      title: "Επιτυχία!",
      text: "Επιτυχής διαγραφή εταιρείας!",
    });
    refreshTable();
  };

  const handleEdit = () => {
    navigate(`/companies/companies-modify/${companyId}`);
  };

  const handleViewDocument = () => {
    navigate(`/invoices/invoices-list?company=${companyId}`);
  };

  const handleTopUp = () => {
    Swal.fire({
      icon: "info",
      title: "Προσθήκη παραστατικών",
      text: "Πληκτρολογήστε τον αριθμό παραστατικών που θέλετε να προσθέσετε στην εταιρεία.",
      input: "number",
      showCancelButton: true,
      cancelButtonText: "ΑΚΥΡΟ",
      confirmButtonText: "ΠΡΟΣΘΗΚΗ",
      inputAttributes: {
        autocapitalize: "off",
      },
      preConfirm: async (credits) => {
        const response = await ApiService.postData(`/api/v1/companies/${companyId}/top-up`, {
          credits: Number(credits),
        });

        if (!response) {
          Swal.fire({
            icon: "error",
            title: "Αποτυχία!",
            text: "Αποτυχία προσθήκης παραστατικών!",
          });
          return false;
        }
        setInvoiceCount(invoiceCount - Number(credits));
        return true;
      },
    }).then((result) => {
      if (!result.isConfirmed) return;
      Swal.fire({
        icon: "success",
        title: "Επιτυχία!",
        text: "Επιτυχής προσθήκη παραστατικών!",
      });
      refreshTable();
    });
  };
  return (
    <SoftBox display="flex" alignItems="center">
      {accessLevel === Constants.ACCESS_LEVELS.USER && apiKey !== null && (
        <SoftTypography
          variant="body1"
          color="secondary"
          sx={{ cursor: "pointer", lineHeight: 0 }}
          onClick={handleTopUp}
        >
          <Tooltip title="Προσθήκη παραστατικών" placement="left">
            <Icon>add</Icon>
          </Tooltip>
        </SoftTypography>
      )}
      <SoftTypography
        variant="body1"
        color="secondary"
        sx={{ cursor: "pointer", lineHeight: 0 }}
        onClick={handleViewDocument}
      >
        <Tooltip title="Προβολή Παραστατικών" placement="top">
          <Icon>article</Icon>
        </Tooltip>
      </SoftTypography>
      <SoftTypography
        variant="body1"
        color="secondary"
        sx={{ cursor: "pointer", lineHeight: 0 }}
        onClick={handleOpenCreditTransactions}
      >
        <Tooltip title="Συναλλαγές credits" placement="top">
          <History />
        </Tooltip>
      </SoftTypography>
      {apiKey !== null ? (
        <>
          <SoftTypography
            variant="body1"
            color="secondary"
            sx={{ cursor: "pointer", lineHeight: 0 }}
            onClick={clickApiKey}
          >
            <Tooltip title="Api Key" placement="top">
              <Icon>key</Icon>
            </Tooltip>
          </SoftTypography>
        </>
      ) : (
        ""
      )}
      <SoftTypography
        variant="body1"
        color="secondary"
        sx={{ cursor: "pointer", lineHeight: 0 }}
        onClick={handleEdit}
      >
        <Tooltip title="Επεξεργασία" placement="top">
          <Icon>edit</Icon>
        </Tooltip>
      </SoftTypography>
      <SoftTypography
        variant="body1"
        color="secondary"
        sx={{ cursor: "pointer", lineHeight: 0 }}
        onClick={handleDelete}
      >
        <Tooltip title="Διαγραφή" placement="left">
          <Icon>delete</Icon>
        </Tooltip>
      </SoftTypography>
      <Dialog
        open={creditDialogOpen}
        onClose={handleCloseCreditTransactions}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Συναλλαγές Credits - {companyName}</DialogTitle>
        <DialogContent>
          <TableContainer sx={{ boxShadow: "none" }}>
            <Table>
              <SoftBox component="thead">
                <TableRow>
                  <DataTableHeadCell>ID</DataTableHeadCell>
                  <DataTableHeadCell>CREDITS</DataTableHeadCell>
                  <DataTableHeadCell>ΥΠΟΛΟΙΠΟ</DataTableHeadCell>
                  <DataTableHeadCell>ΧΡΗΣΤΗΣ</DataTableHeadCell>
                  <DataTableHeadCell>ΗΜΕΡΟΜΗΝΙΑ</DataTableHeadCell>
                </TableRow>
              </SoftBox>
              <TableBody>
                {creditTransactions.map((transaction) => (
                  <TableRow
                    key={transaction.id}
                    sx={{
                      backgroundColor:
                        transaction.credits > 0
                          ? badgeColors.success.background
                          : transaction.credits < 0
                          ? badgeColors.error.background
                          : "transparent",
                    }}
                  >
                    <DataTableBodyCell>{transaction.id}</DataTableBodyCell>
                    <DataTableBodyCell>
                      <SoftTypography variant="button" fontWeight="bold" color="text">
                        {transaction.credits}
                      </SoftTypography>
                    </DataTableBodyCell>
                    <DataTableBodyCell>{transaction.targetBalanceAfter}</DataTableBodyCell>
                    <DataTableBodyCell>{transaction.actorUserName}</DataTableBodyCell>
                    <DataTableBodyCell>{transaction.createdAt}</DataTableBodyCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <SoftButton variant="gradient" color="secondary" onClick={handleCloseCreditTransactions}>
            ΚΛΕΙΣΙΜΟ
          </SoftButton>
        </DialogActions>
      </Dialog>
    </SoftBox>
  );
}

export default CompaniesActionCell;
